import React from 'react';
import { SOCIALS, LOGO } from '../mock';
import { Linkedin, Instagram, Facebook, ExternalLink, Users, ThumbsUp } from 'lucide-react';
import useReveal from '../hooks/useReveal';

/* ============ FACEBOOK PREVIEW PANEL ============ */
/* Note: The Facebook Page Plugin requires the canonical Page URL (e.g. https://www.facebook.com/vistorabranding).
   The provided share URL (facebook.com/share/...) does not resolve for the Plugin.
   This panel styles a Facebook-native preview and links to the profile. */
function FacebookPanel() {
  return (
    <div style={panelStyle}>
      <PanelHeader color="#1877F2" name="FACEBOOK" sub="COMPANY PAGE" Icon={Facebook} url={SOCIALS.facebook} />
      <div style={{ background: '#fff', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Cover */}
        <div style={{
          height: 110,
          background: 'linear-gradient(120deg, #1877F2 0%, #4267B2 50%, #385898 100%)',
          position: 'relative',
        }}>
          <div style={{
            position: 'absolute', top: 16, right: 16,
            background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)',
            color: '#fff', fontSize: 10, fontFamily: 'Segoe UI, system-ui, sans-serif', fontWeight: 600,
            padding: '4px 10px', borderRadius: 999,
          }}>Page · Corporate</div>
        </div>
        <div style={{ padding: '0 20px 20px', marginTop: -34, flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Circle avatar (FB style) */}
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: '#fff', border: '4px solid #fff', padding: 3,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}>
            <img src={LOGO} alt="Vistora" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%' }} />
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={{ color: '#050505', fontSize: 18, fontWeight: 700, fontFamily: 'Segoe UI, system-ui, sans-serif' }}>Vistora Branding</div>
            <div style={{ color: '#65676B', fontSize: 12, marginTop: 3, fontFamily: 'Segoe UI, system-ui, sans-serif' }}>Corporate Signage &amp; Branding Studio</div>
            <div style={{ color: '#65676B', fontSize: 11, marginTop: 6, fontFamily: 'Segoe UI, system-ui, sans-serif', display: 'flex', alignItems: 'center', gap: 6 }}>
              <ThumbsUp size={11} strokeWidth={2} /> Bengaluru · Pan-India Presence
            </div>
          </div>

          {/* Mini post rows */}
          <div style={{ marginTop: 16, borderTop: '1px solid #E4E6EB', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { tag: 'Reception feature wall', body: 'Backlit brass mount unveiling for a corporate client.' },
              { tag: 'Facade rebrand', body: 'ACP composite panels for a Whitefield HQ.' },
              { tag: 'Team spotlight', body: 'Meet our senior partners driving corporate rollouts.' },
            ].map((p, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 34, height: 34, borderRadius: 6, background: '#F0F2F5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <img src={LOGO} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#050505', fontSize: 12, fontWeight: 600, fontFamily: 'Segoe UI, system-ui, sans-serif' }}>{p.tag}</div>
                  <div style={{ color: '#65676B', fontSize: 11, lineHeight: 1.4, marginTop: 2, fontFamily: 'Segoe UI, system-ui, sans-serif' }}>{p.body}</div>
                </div>
              </div>
            ))}
          </div>

          <a href={SOCIALS.facebook} target="_blank" rel="noopener noreferrer" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            width: '100%', padding: '10px 16px', marginTop: 18,
            background: '#1877F2', color: '#fff',
            borderRadius: 6,
            textDecoration: 'none',
            fontFamily: 'Segoe UI, system-ui, sans-serif',
            fontSize: 13, fontWeight: 700,
            transition: 'background 0.25s',
          }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#0d5dbf'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#1877F2'}
          >
            <ThumbsUp size={13} strokeWidth={2} /> Like &amp; Follow
          </a>
        </div>
      </div>
      <FootNote text="FACEBOOK PAGE PLUGIN NEEDS CANONICAL PAGE URL · OPENS SHARE LINK" />
    </div>
  );
}

/* ============ INSTAGRAM PREVIEW PANEL ============ */
function InstagramPanel() {
  return (
    <div style={panelStyle}>
      <PanelHeader
        gradient="linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4)"
        name="INSTAGRAM" sub="@vistora_branding_solutions" Icon={Instagram} url={SOCIALS.instagram}
      />
      <div style={{ padding: '28px 20px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, flex: 1 }}>
        <div style={{
          width: 90, height: 90, borderRadius: '50%', padding: 3,
          background: 'linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4)',
        }}>
          <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: 'var(--warm-white)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={LOGO} alt="Vistora" style={{ width: 68, height: 68, objectFit: 'contain' }} />
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div className="font-display" style={{ color: 'var(--paper)', fontSize: 22, letterSpacing: '-0.01em' }}>Vistora Branding</div>
          <div className="font-mono" style={{ color: 'var(--muted)', fontSize: 10, letterSpacing: '0.2em', marginTop: 4 }}>@VISTORA_BRANDING_SOLUTIONS</div>
        </div>

        <div className="grid grid-cols-3 gap-1.5" style={{ width: '100%', marginTop: 6 }}>
          {[
            { c: '#E97435', label: 'Lobby' },
            { c: '#2A9AA0', label: 'Facade' },
            { c: '#1F3438', label: 'Neon' },
            { c: '#4FBFB8', label: 'Wayfind' },
            { c: '#B85920', label: 'Print' },
            { c: '#0F1F22', label: 'Team' },
          ].map((t, i) => (
            <a key={i} href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" style={{
              aspectRatio: '1 / 1',
              background: `linear-gradient(135deg, ${t.c}, ${t.c}88)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative', overflow: 'hidden', borderRadius: 4,
              textDecoration: 'none', transition: 'transform 0.3s',
            }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.04)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <img src={LOGO} alt="" style={{ width: '55%', height: '55%', objectFit: 'contain', opacity: 0.85 }} />
              <span className="font-mono" style={{ position: 'absolute', bottom: 6, left: 6, color: '#fff', fontSize: 8, letterSpacing: '0.16em', textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>{t.label.toUpperCase()}</span>
            </a>
          ))}
        </div>

        <a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          width: '100%', padding: '12px 16px', marginTop: 8,
          background: 'linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4)',
          color: '#fff', borderRadius: 999, textDecoration: 'none',
          fontFamily: 'Space Grotesk, sans-serif', fontSize: 12, fontWeight: 600,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          transition: 'transform 0.25s',
        }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = ''}
        >
          <Instagram size={14} strokeWidth={2} /> Follow on Instagram
        </a>
      </div>
      <FootNote text="INSTAGRAM PROFILE-FEED EMBED IS NOT OFFERED PUBLICLY · OPENS NATIVE APP" />
    </div>
  );
}

/* ============ LINKEDIN PREVIEW PANEL ============ */
function LinkedInPanel() {
  return (
    <div style={panelStyle}>
      <PanelHeader color="#0A66C2" name="LINKEDIN" sub="COMPANY PAGE" Icon={Linkedin} url={SOCIALS.linkedin} />
      <div style={{ background: '#fff', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ height: 100, background: 'linear-gradient(120deg, #0A66C2 0%, #2A9AA0 100%)', position: 'relative' }} />
        <div style={{ padding: '0 20px 24px', marginTop: -40, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{
            width: 80, height: 80, borderRadius: 8, background: '#fff',
            border: '3px solid #fff', padding: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}>
            <img src={LOGO} alt="Vistora" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <div style={{ marginTop: 14 }}>
            <div style={{ color: '#000', fontSize: 18, fontWeight: 700, fontFamily: 'Segoe UI, system-ui, sans-serif' }}>Vistora Branding Solutions</div>
            <div style={{ color: '#666', fontSize: 12, marginTop: 4, fontFamily: 'Segoe UI, system-ui, sans-serif' }}>Corporate Signage &amp; Branding Studio</div>
            <div style={{ color: '#0A66C2', fontSize: 11, marginTop: 6, fontFamily: 'Segoe UI, system-ui, sans-serif' }}>Bengaluru, India · Pan-India Presence</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, color: '#666', fontSize: 11, fontFamily: 'Segoe UI, system-ui, sans-serif' }}>
              <Users size={12} strokeWidth={2} /> Signage · Branding · Turnkey Installations
            </div>
          </div>

          <div style={{ marginTop: 18, borderTop: '1px solid #eee', paddingTop: 14 }}>
            <div style={{ color: '#666', fontSize: 10, fontFamily: 'Segoe UI, system-ui, sans-serif', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10, fontWeight: 600 }}>
              About
            </div>
            <p style={{ color: '#333', fontSize: 12, lineHeight: 1.55, fontFamily: 'Segoe UI, system-ui, sans-serif' }}>
              A premium corporate signage and branding studio crafting dimensional letters, lobby monoliths, ACP facades, LED boards, wall graphics, and turnkey installations — delivered nationwide.
            </p>
          </div>

          <a href={SOCIALS.linkedin} target="_blank" rel="noopener noreferrer" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            width: '100%', padding: '11px 16px', marginTop: 20,
            background: '#0A66C2', color: '#fff', borderRadius: 999,
            textDecoration: 'none', fontFamily: 'Segoe UI, system-ui, sans-serif',
            fontSize: 13, fontWeight: 700, transition: 'background 0.25s',
          }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#004182'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#0A66C2'}
          >
            <Linkedin size={14} strokeWidth={2} fill="#fff" /> Follow on LinkedIn
          </a>
        </div>
      </div>
      <FootNote text="LINKEDIN RETIRED PUBLIC COMPANY BADGES IN 2022 · OPENS NATIVE PAGE" />
    </div>
  );
}

/* ============ Shared UI ============ */
const panelStyle = {
  background: 'var(--ink-2)',
  border: '1px solid var(--line)',
  borderRadius: 12,
  overflow: 'hidden',
  position: 'relative',
  minHeight: 620,
  display: 'flex',
  flexDirection: 'column',
};

function PanelHeader({ color, gradient, name, sub, Icon, url }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 18px', borderBottom: '1px solid var(--line)',
      background: `linear-gradient(180deg, ${color ? color + '18' : 'rgba(225, 48, 108, 0.10)'}, transparent)`,
    }}>
      <div className="flex items-center gap-3">
        <div style={{ width: 32, height: 32, borderRadius: name === 'LINKEDIN' ? 4 : '50%', background: gradient || color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={16} color="#fff" strokeWidth={2} fill={name === 'LINKEDIN' || name === 'FACEBOOK' ? '#fff' : 'none'} />
        </div>
        <div>
          <div className="font-mono" style={{ color: 'var(--paper)', fontSize: 11, letterSpacing: '0.16em', fontWeight: 600 }}>{name}</div>
          <div className="font-mono" style={{ color: 'var(--muted)', fontSize: 9, letterSpacing: '0.14em' }}>{sub}</div>
        </div>
      </div>
      <a href={url} target="_blank" rel="noopener noreferrer" style={{
        color: 'var(--teal-light)', display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 11, textDecoration: 'none', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.14em',
      }}>
        OPEN <ExternalLink size={12} strokeWidth={2} />
      </a>
    </div>
  );
}

function FootNote({ text }) {
  return (
    <div className="font-mono" style={{
      color: 'var(--muted)', fontSize: 9, letterSpacing: '0.18em',
      textAlign: 'center', padding: '10px 12px',
      borderTop: '1px solid var(--line)', textTransform: 'uppercase',
    }}>{text}</div>
  );
}

/* ============ MAIN SECTION ============ */
export default function SocialFeed() {
  const [ref, inView] = useReveal(0.08);
  const [lRef, lIn] = useReveal(0.05);
  const [iRef, iIn] = useReveal(0.05);
  const [fRef, fIn] = useReveal(0.05);

  return (
    <section ref={ref} id="social" className="section" style={{ background: 'var(--ink-2)', position: 'relative' }}>
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-8 items-end" style={{ marginBottom: 50 }}>
          <div className={`lg:col-span-7 reveal ${inView ? 'in-view' : ''}`}>
            <div className="eyebrow" style={{ marginBottom: 24 }}>§ 06 · Latest From Social</div>
            <h2 className="section-title">Recently <em>posted</em>.</h2>
          </div>
          <div className={`lg:col-span-5 reveal delay-2 ${inView ? 'in-view' : ''}`}>
            <p className="font-serif-i" style={{ color: 'var(--smoke)', fontSize: 17, lineHeight: 1.6 }}>
              Native profile previews for LinkedIn, Instagram &amp; Facebook — tap any panel to see the live feed on the platform.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div ref={lRef} className={`reveal delay-1 ${lIn ? 'in-view' : ''}`}><LinkedInPanel /></div>
          <div ref={iRef} className={`reveal delay-2 ${iIn ? 'in-view' : ''}`}><InstagramPanel /></div>
          <div ref={fRef} className={`reveal delay-3 ${fIn ? 'in-view' : ''}`}><FacebookPanel /></div>
        </div>
      </div>
    </section>
  );
}
